import { effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private static readonly KEY = 'app-theme';

  readonly mode = signal<ThemeMode>(this.readStoredMode());

  constructor() {
    effect(() => this.applyMode(this.mode()));

    if (isPlatformBrowser(this.platformId)) {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', () => {
          if (this.mode() === 'system') this.applyMode('system');
        });
    }
  }

  setMode(mode: ThemeMode): void {
    localStorage.setItem(ThemeService.KEY, mode);
    this.mode.set(mode);
  }

  private readStoredMode(): ThemeMode {
    if (!isPlatformBrowser(this.platformId)) return 'light';
    const stored = localStorage.getItem(ThemeService.KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  }

  private applyMode(mode: ThemeMode): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const dark =
      mode === 'dark' ||
      (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.setAttribute(
      'data-theme',
      dark ? 'dark' : 'light',
    );
  }
}
